import { zipObject } from "lodash";

import { ResponseKind } from "@/pages/brand-equity/components/constants";

interface SourceData {
  brand_equity: {
    dollar: BrandEquity;
    percent: BrandEquity;
  };
  brand_metric: BrandMetric;
  date: string[];
  kind: ResponseKind;
  sample_size: number;
  total: {
    brand_equity_sale: number;
  };
}

interface GraphInput {
  brand_equity: BrandEquity;
  brand_metric: BrandMetric;
  date: string[];
  total: number;
  tableHeaders?: string[];
  tableRows?: { [key: string]: number | string }[];
}

interface BrandMetric {
  [key: string]: number[];
}

interface BrandEquity {
  brand_equity_sale: number[];
}

export const getBrandEquityInput = (
  sourceData: SourceData,
  isDollarData: boolean,
  currencySymbol: string,
  showReturnInsteadOfRevenue: boolean = false
): GraphInput | null => {
  // TODO: Evaluate if this check is necessary
  if (!sourceData?.date || sourceData.date.length === 0) {
    return null;
  }

  const subItem = isDollarData ? "dollar" : "percent";
  const sign = isDollarData ? currencySymbol : "%";
  const brandEquity = sourceData.brand_equity[subItem];
  const brandMetric = sourceData.brand_metric;

  const metricNames = Object.keys(brandMetric).filter((name) => name.toLowerCase() !== "intercept");
  const equityName = getBrandEquityLabel(sign, showReturnInsteadOfRevenue ? "return" : "sales");
  const tableHeaders = ["Date", equityName, ...metricNames];
  const tableRows: { [key: string]: number | string }[] = [];

  sourceData.date.forEach((dateItem, index) => {
    const metricRows = metricNames.map((name) => brandMetric[name]![index]!);
    const equityRow = brandEquity.brand_equity_sale[index]!;

    tableRows.push(zipObject(tableHeaders, [dateItem, equityRow, ...metricRows]));
  });

  return {
    brand_equity: brandEquity,
    brand_metric: brandMetric,
    date: sourceData.date,
    total: sourceData.total.brand_equity_sale,
    tableHeaders: tableHeaders,
    tableRows: tableRows,
  };
};

export const getBrandEquityPlaceholderInput = (currencySymbol: string): GraphInput => {
  return {
    brand_equity: {
      brand_equity_sale: [10304570.707531814, 11256629.64357549, 7042057.454446184],
    },
    brand_metric: {
      awareness: [2400, 3600, 6500],
      consideration: [48000, 13000, 3000],
    },
    date: ["2022-04-01", "2022-05-01", "2022-06-01"],
    total: 21424050,
    tableHeaders: ["Date", `Total Sales ${currencySymbol}`, "awareness", "consideration"],
    tableRows: [
      {
        Date: "2022-04-01",
        [getBrandEquityLabel(currencySymbol)]: 10304570.707531814,
        awareness: 2400,
        consideration: 48000,
      },
      {
        Date: "2022-05-01",
        [getBrandEquityLabel(currencySymbol)]: 11256629.64357549,
        awareness: 3600,
        consideration: 13000,
      },
      {
        Date: "2022-06-01",
        [getBrandEquityLabel(currencySymbol)]: 7042057.454446184,
        awareness: 6500,
        consideration: 3000,
      },
    ],
  };
};

const getBrandEquityLabel = (sign: string, returnType: "sales" | "return" = "sales") =>
  `Total ${returnType} ${sign}`;
